/* eslint-disable prettier/prettier */
import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Icon,
  SearchField,
  Checkbox,
  ButtonIcon,
  Typography,
} from '@statsbomb/kitbag-components';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { AccordionSaveRow } from '../../Scout.styles';
import {
  MetricModalContent,
  ModalMetricHeader,
  ModalSearchHolder,
  MetricModalCheckboxContainer,
  MetricModalItem,
  MetricModalItemLabel,
  MetricModalAdjuster,
  MetricModalStatusWrapper
} from './MetricModal.styles';
import { sortStatsBySearch } from './MetricModal.dataManipulation';
import { useGetPlayerStatDistribution } from './useGetPlayerStatDistribution';
import LoaderInline from '../../../../components/LoaderInline/LoaderInline';
import { DEFAULT_SCOUT_POSITION } from '../ScoutResults.constants';
import { PLAYS_STAT_KEY } from '../ScoutPlayers/ScoutPlayers.constants';
import { formatPlayerStatDefinitionsForModal } from '../ScoutResults.dataManipulation';
import { useStore } from '../../../../store/useStore';
import { trackObjectOpenCloseEvent } from '../../../../utils/helpers/snowplow';

const MetricModal = ({__storybookMetricModalOpen, __storybookSearchValue}) => {
  const theme = useTheme();
  const [ isOpen, setIsOpen ] = useState(__storybookMetricModalOpen || false);
  const filterId = 'metric-modal';
  const title = 'Add Metric Filter';
  const statDefinitionsInStore = useStore((s) => s.statDefinitions);
  const statDefinitionsLoading = useStore((s) => s.statDefinitionsLoading);
  const selectedStats = useStore((s) => s.selectedStats);
  const selectedPositions = useStore((s) => s.selectedPositions);
  const updatePlayerMetric = useStore((s) => s.updatePlayerMetric);
  // toast
  const setToast = useStore((s) => s.setToast);

  // array of selected metric keys
  const statDefinitions = formatPlayerStatDefinitionsForModal(statDefinitionsInStore);
  const [selectedMetricsKeys, setSelectedMetricsKeys] = useState([]);
  const noStats = isEmpty(statDefinitions);
  const nonPlaysStats = selectedStats?.filter((s) => s.key !== PLAYS_STAT_KEY);
  const selectedStatKeys = nonPlaysStats?.map((m) => m?.key);

  const {
    metricStatDistribution,
    loading: isLoading,
    error: hasError,
  } = useGetPlayerStatDistribution(
    selectedMetricsKeys,
    selectedPositions[0] || DEFAULT_SCOUT_POSITION,
    statDefinitions
  );

  const addMetricKey = (key) => {
    setSelectedMetricsKeys((prev) => [...prev, key]);
  };

  const removeMetricKey = (key) => {
    setSelectedMetricsKeys((prev) => prev.filter((item) => item !== key));
  };

  const [searchValue, setSearchValue] = useState(__storybookSearchValue || '');
  const searchedMetrics = sortStatsBySearch(statDefinitions, searchValue);

  // ensure that the checked items are in sync with the parent selectedStats
  useEffect(() => {
    setSelectedMetricsKeys(selectedStatKeys);
  }, [isOpen]);

  // ensure that an error fetching stats resets selection
  useEffect(() => {
    if (hasError) {
        setToast(
        {
          title: 'Error',
          message: 'Metric range data could not be found.',
          type: 'error',
        },
        true
      );
      setSelectedMetricsKeys((prev) => prev.slice(0, -1));
    }
  }, [hasError]);

  // Analytics tracking for modal open/close
  const prevIsOpen = useRef(isOpen);
  useEffect(() => {
    if (prevIsOpen.current !== isOpen) {
      trackObjectOpenCloseEvent('List View', isOpen, 'Metric Modal');
      prevIsOpen.current = isOpen;
    }
  }, [isOpen]);

  const handleMetricModalItemClick = (metric) => {
    if (selectedMetricsKeys.includes(metric.key)) {
      removeMetricKey(metric.key);
    } else {
      addMetricKey(metric.key);
    }
  }

  return (
    <>
      <AccordionSaveRow>
        {statDefinitionsLoading && (
          <LoaderInline text="Available Metrics Loading" />
        )}
        {!statDefinitionsLoading && noStats && (
          <span>Error Loading Metric List</span>
        )}
        {!statDefinitionsLoading && !noStats && (
          <Button
            variant="secondary"
            size="small"
            isDisabled={noStats}
            onClick={() => {
              setIsOpen(true);
            }}
          >
            <span>
              <Icon variant="PlusAdd" size="small" />
            </span>
            Add Metric Filter
          </Button>
        )}
      </AccordionSaveRow>
      <MetricModalAdjuster>
        <Modal id={filterId} title={title} isOpen={isOpen}>
          <ModalHeader id={`${filterId}-header`}>
            <ModalMetricHeader>
              <div>
                <div>{title}</div>
                <div style={{ position: 'absolute', right: '1rem', top: '1rem' }}>                
                  <ButtonIcon
                    onClick={() => {
                    setIsOpen(false);
                    setSearchValue('');
                  }}
                    variant="ghost"
                    shape="pill"
                    size="small"
                    icon="Close"
                    title="Cancel"
                  />
                </div>
              </div>
              <ModalSearchHolder>
                <SearchField
                  value={searchValue}
                  isLoading={isLoading}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search for a metric filter"
                  searchIconTitle="search"
                  onClear={() => setSearchValue('')}
                />
              </ModalSearchHolder>
            </ModalMetricHeader>
          </ModalHeader>
          <ModalBody testId="metric-modal-body">
            <MetricModalContent>
              <MetricModalCheckboxContainer>
                {Object.entries(
                searchedMetrics.reduce((acc, metric) => {
                  if (!acc[metric.subcategory]) acc[metric.subcategory] = [];
                  acc[metric.subcategory].push(metric);
                  return acc;
                }, {})
              ).map(([subcategory, metrics]) => (
                <div key={subcategory} style={{ marginBottom: theme.sizing.large }}>
                  <Typography variant="headingMedium" style={{ margin: `0 0 ${theme.sizing.small} ${theme.sizing.small}` }}>
                    {subcategory}
                  </Typography>
                  <div>
                    {metrics.map((metric) => (
                      <MetricModalItem
                        key={metric.key}
                        onClick={() => handleMetricModalItemClick(metric)}
                      >
                        <Checkbox
                          id={`metric-${metric.abbreviation}-checkbox-${metric.index}`}
                          onChange={(e) =>
                          e.target.checked
                            ? addMetricKey(metric.key)
                            : removeMetricKey(metric.key)}
                          checked={selectedMetricsKeys.includes(metric.key)}
                        />
                        <MetricModalItemLabel>
                          <span>{metric.abbreviation}</span>
                          <span>{metric.description}</span>
                        </MetricModalItemLabel>
                      </MetricModalItem>
                      )
                  )}
                  </div>
                </div>
              ))}
              </MetricModalCheckboxContainer>

              {searchedMetrics?.length === 0 && !isLoading && (
              <MetricModalStatusWrapper>
                <Icon variant="Info" size="regular" />
                <Typography variant="headingLarge">No Results</Typography>
                <Typography
                  variant="bodyRegular"
                  data-testid="no-results-text"
                >
                  Try adjusting your search term
                </Typography>
              </MetricModalStatusWrapper>
              )}
            </MetricModalContent>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              onClick={() => {
              setIsOpen(false);
              setSearchValue('');
            }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
              updatePlayerMetric(metricStatDistribution);
              setIsOpen(false);
            }}
              isDisabled={hasError || isLoading}
            >
              Apply
            </Button>
          </ModalFooter>
        </Modal>
      </MetricModalAdjuster>
    </>
  );
};
MetricModal.propTypes = {
  __storybookMetricModalOpen: PropTypes.bool,
  __storybookSearchValue: PropTypes.string,
};

export default MetricModal;
